import type {
  CompletedHandRecord,
  PlayerStatsSummary,
  RecentTrendPoint,
  StatsSampleQuality,
  TrumpDistributionEntry,
} from './types.js';

const SMALL_SAMPLE_HANDS = 10;
const RECENT_TREND_LIMIT = 12;

function trumpLabel(hand: CompletedHandRecord): string {
  if (hand.trumpDeclaration === 'suit' && hand.trumpSuit !== null) {
    const suits = ['Blanks', 'Ones', 'Twos', 'Threes', 'Fours', 'Fives', 'Sixes'];
    return suits[hand.trumpSuit] ?? `Suit ${hand.trumpSuit}`;
  }
  switch (hand.trumpDeclaration) {
    case 'doubles':
      return 'Doubles';
    case 'nello':
      return 'Nello';
    case 'sevens':
      return 'Sevens';
    case 'marks':
      return 'Marks';
    default:
      return 'Unknown';
  }
}

function sampleQuality(totalHands: number): StatsSampleQuality {
  if (totalHands === 0) return 'none';
  if (totalHands < SMALL_SAMPLE_HANDS) return 'small';
  return 'adequate';
}

function rate(numerator: number, denominator: number): number | null {
  if (denominator === 0) return null;
  return numerator / denominator;
}

/**
 * Rebuild player stats from append-only completed-hand records.
 */
export function derivePlayerStatsSummary(hands: CompletedHandRecord[]): PlayerStatsSummary {
  const countable = hands.filter((hand) => !hand.isPracticeHand);
  const sorted = [...countable].sort((left, right) =>
    left.completedAt.localeCompare(right.completedAt),
  );

  const matchIds = new Set<string>();
  const matchWins = new Set<string>();

  let handsBid = 0;
  let handsDefended = 0;
  let contractsMade = 0;
  let defenderSets = 0;
  let successfulBidTotal = 0;
  let successfulBidCount = 0;
  let marksFor = 0;
  let marksAgainst = 0;

  const trumpCounts = new Map<string, number>();

  for (const hand of sorted) {
    matchIds.add(hand.matchId);
    if (hand.matchCompleted && hand.humanWonMatch) {
      matchWins.add(hand.matchId);
    }

    marksFor += hand.marksAwarded[0] ?? 0;
    marksAgainst += hand.marksAwarded[1] ?? 0;

    const trumpKey = trumpLabel(hand);
    trumpCounts.set(trumpKey, (trumpCounts.get(trumpKey) ?? 0) + 1);

    if (hand.humanWasBidder) {
      handsBid += 1;
      if (hand.contractMade) {
        contractsMade += 1;
        if (hand.bidValue !== null) {
          successfulBidTotal += hand.bidValue;
          successfulBidCount += 1;
        }
      }
    } else {
      handsDefended += 1;
      if (hand.contractMade) {
        defenderSets += 1;
      }
    }
  }

  const totalHands = sorted.length;
  const trumpTotal = [...trumpCounts.values()].reduce((sum, count) => sum + count, 0);
  const trumpDistribution: TrumpDistributionEntry[] = [...trumpCounts.entries()]
    .map(([label, count]) => ({
      label,
      count,
      share: trumpTotal === 0 ? 0 : count / trumpTotal,
    }))
    .sort((left, right) => right.count - left.count);

  const recentTrend: RecentTrendPoint[] = sorted.slice(-RECENT_TREND_LIMIT).map((hand) => ({
    handId: hand.id,
    completedAt: hand.completedAt,
    success: hand.contractMade,
  }));

  return {
    matchesPlayed: matchIds.size,
    matchesWon: matchWins.size,
    matchWinRate: rate(matchWins.size, matchIds.size),
    handsBid,
    handsDefended,
    contractSuccessRate: rate(contractsMade, handsBid),
    defenderSetRate: rate(defenderSets, handsDefended),
    averageSuccessfulBid: rate(successfulBidTotal, successfulBidCount),
    marksFor,
    marksAgainst,
    trumpDistribution,
    recentTrend,
    totalHands,
    sampleQuality: sampleQuality(totalHands),
  };
}
